import React, { useEffect, useState } from "react";
import { Box, Grid, TextField, Button, CircularProgress } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import FlexBetween from "../../components/FlexBetween";
import Loader from "../../components/Molecules/Loader/Loader";
import CommentBeforeUpdateDialog from "../../components/Dialogs/CommentBeforeUpdateDialog";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useToast, extractApiErrorMessage } from "../../services/utils";
import { getDifferentValues } from "../../services/utils/processRuleExecutionResourcesData";
import {
  createPipelineWeightConfig,
  updatePipelineWeightConfig,
} from "../../services/scoringService";
import useFetchPipelineWeightConfigByRunId from "../../hooks/useFetchPipelineWeightConfigById";
import { pipelineWeightConfigDefaults } from "../../services/constants";
import { pipelineWeightConfigFormSchema } from "../../schemas";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import AutoCompleteRunList from "../../components/Molecules/Run/AutoCompleteRunList";
import { getRunInstanceDetail } from "../../services/runInstance";

const AddEditPipelineWeightConfig: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  // In edit/view flows the `:id` URL segment carries the run_instance_id
  // (matches GET /run-scoring-pipeline-weight-config/{run_instance_id}).
  const { id: runInstanceId } = useParams();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const isEditMode = Boolean(runInstanceId);

  const [isLoading, setIsLoading] = useState(false);
  const [openComment, setOpenComment] = useState(false);
  const [comment, setComment] = useState("");
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [runId, setRunId] = useState<number | "">("");
  const [runError, setRunError] = useState<string>("");
  const [runName, setRunName] = useState<string>("");
  // recordId holds the config record's primary key (needed for PUT/DELETE).
  // Seeded from list-page state so it is available even before the async
  // fetch resolves; overwritten once the API response arrives.
  const [recordId, setRecordId] = useState<number | undefined>(
    location.state?.rowData?.id ?? undefined
  );
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  const [configData] = useFetchPipelineWeightConfigByRunId({
    setIsLoading,
    runInstanceId: runInstanceId ? parseInt(runInstanceId) : undefined,
  });
  const effectiveConfig =
    isEditMode && location.state?.rowData?.id && !configData?.id
      ? location.state.rowData
      : configData;

  const formik = useFormik({
    initialValues: {
      comparison_weight:
        effectiveConfig?.comparison_weight ??
        pipelineWeightConfigDefaults.comparison_weight,
      validation_weight:
        effectiveConfig?.validation_weight ??
        pipelineWeightConfigDefaults.validation_weight,
      research_query_weight:
        effectiveConfig?.research_query_weight ??
        pipelineWeightConfigDefaults.research_query_weight,
    },
    validationSchema: pipelineWeightConfigFormSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!isEditMode) {
        if (!runId) {
          setRunError("Execution Group is required");
          return;
        }
        setIsLoading(true);
        try {
          await createPipelineWeightConfig({
            run_instance_id: Number(runId),
            comparison_weight: Number(values.comparison_weight),
            validation_weight: Number(values.validation_weight),
            research_query_weight: Number(values.research_query_weight),
          });
          showToast("Pipeline Weight Config created!", "success");
          navigate("/scoring-config?tab=1&aggSub=3");
        } catch (error) {
          showToast(
            extractApiErrorMessage(error, "Failed to create config"),
            "error"
          );
        } finally {
          setIsLoading(false);
        }
      } else {
        const diff = getDifferentValues(values, formik.initialValues);
        if (!diff || Object.keys(diff).length === 0) {
          showToast("No changes detected", "warning");
          return;
        }
        setPendingPayload(values);
        setOpenComment(true);
      }
    },
  });

  // Breadcrumb
  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: isEditMode
        ? "Edit Pipeline Weight Config"
        : "Add Pipeline Weight Config",
      id: runInstanceId ? parseInt(runInstanceId) : null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [runInstanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture the record's primary key from API once available
  useEffect(() => {
    if (configData?.id) {
      setRecordId(configData.id);
    }
  }, [configData]);

  // Sync execution group id from effective config (edit mode pre-fill)
  useEffect(() => {
    if (effectiveConfig?.run_instance_id) {
      setRunId(effectiveConfig.run_instance_id);
    }
  }, [effectiveConfig]);

  // Fetch execution group display name for read-only edit-mode field
  useEffect(() => {
    if (!isEditMode || !runId) return;
    (async () => {
      try {
        const data = await getRunInstanceDetail({
          currentRunInstanceId: runId,
        });
        if (data?.run_name) setRunName(data.run_name);
      } catch (err) {
        console.error("Failed to load execution group name:", err);
      }
    })();
  }, [isEditMode, runId]);

  // Pre-fill weight fields once the config record is available.
  // Uses formik.setValues so the form picks up async data even when the
  // initial render fell back to defaults (e.g. direct URL access / refresh).
  useEffect(() => {
    if (!isEditMode || !configData?.id) return;
    formik.resetForm({
      values: {
        comparison_weight:
          configData.comparison_weight ??
          pipelineWeightConfigDefaults.comparison_weight,
        validation_weight:
          configData.validation_weight ??
          pipelineWeightConfigDefaults.validation_weight,
        research_query_weight:
          configData.research_query_weight ??
          pipelineWeightConfigDefaults.research_query_weight,
      },
    });
  }, [configData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildPayload = () => {
    if (isEditMode) {
      // Mirrors the body sent by handleSaveComment to updatePipelineWeightConfig.
      // Uses the live `comment` state so the View JSON always reflects the
      // exact payload that would be sent if Save were clicked right now.
      // Include run_instance_id so the payload can be used by Config application.
      return {
        run_instance_id: Number(runId),
        comparison_weight: Number(formik.values.comparison_weight),
        validation_weight: Number(formik.values.validation_weight),
        research_query_weight: Number(formik.values.research_query_weight),
        comment,
      };
    }
    return {
      run_instance_id: Number(runId),
      comparison_weight: Number(formik.values.comparison_weight),
      validation_weight: Number(formik.values.validation_weight),
      research_query_weight: Number(formik.values.research_query_weight),
    };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/run-scoring-pipeline-weight-config/${recordId}`
          : "/run-scoring-pipeline-weight-config",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: String(recordId) } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) showToast("Payload is valid!", "success");
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    await navigator.clipboard.writeText(
      JSON.stringify(buildPayload(), null, 2)
    );
    showToast("Payload copied to clipboard!", "success");
  };

  const handelChangeRun = (_event: any, value: any) => {
    setRunId(value?.id ?? "");
    if (runError) setRunError("");
  };

  const handleSaveComment = async () => {
    setOpenComment(false);
    setIsLoading(true);
    try {
      const payload: any = {};
      Object.keys(pendingPayload).forEach((k) => {
        const val = pendingPayload[k];
        payload[k] = isNaN(Number(val)) ? val : Number(val);
      });
      await updatePipelineWeightConfig(recordId!, {
        ...payload,
        comment,
      });
      showToast("Config updated!", "success");
      navigate(`/scoring/pipeline-weights/view/${runInstanceId}`);
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, "Failed to update config"),
        "error"
      );
    } finally {
      setIsLoading(false);
      setComment("");
      setPendingPayload(null);
    }
  };

  const handleResetToDefaults = () => {
    formik.setValues({
      comparison_weight: pipelineWeightConfigDefaults.comparison_weight,
      validation_weight: pipelineWeightConfigDefaults.validation_weight,
      research_query_weight: pipelineWeightConfigDefaults.research_query_weight,
    });
  };

  const weightField = (
    label: string,
    name: "comparison_weight" | "validation_weight" | "research_query_weight"
  ) => (
    <Grid item xs={12} sm={6} md={4}>
      <label className="label-text">
        {label}
        <span className="required-asterisk">*</span>
      </label>
      <TextField
        name={name}
        type="number"
        inputProps={{ step: 0.01, min: 0 }}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        className={`form-control ${
          formik.touched[name] && formik.errors[name] ? "has-error" : ""
        }`}
        error={formik.touched[name] && Boolean(formik.errors[name])}
        helperText={
          (formik.touched[name] && (formik.errors[name] as string)) || " "
        }
        fullWidth
      />
    </Grid>
  );

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box>
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <Box className="text-box-card-white m-0">
            <Box className="text-box-header">
              <h3>{isEditMode ? "Edit" : "Add"} Pipeline Weight Config</h3>
            </Box>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
            >
              <Grid item xs={12} sm={6} md={4}>
                {isEditMode ? (
                  <>
                    <label className="label-text">Execution Group</label>
                    <TextField
                      fullWidth
                      value={
                        runName
                          ? `(${runId}) ${runName}`
                          : runId
                          ? `(${runId})`
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      className="form-control"
                    />
                  </>
                ) : (
                  <AutoCompleteRunList
                    setIsLoading={setIsLoading}
                    handelOnChangeRun={handelChangeRun}
                    currentRunId={runId || null}
                    className={`form-control-autocomplete ${
                      runError ? "has-error" : ""
                    }`}
                    required
                    error={Boolean(runError)}
                    helperText={runError}
                    listLabel="Execution Group"
                    includeRunId
                  />
                )}
              </Grid>
            </Grid>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
              sx={{ mt: "20px" }}
            >
              {weightField("Comparison Weight", "comparison_weight")}
              {weightField("Validation Weight", "validation_weight")}
              {weightField("Research Query Weight", "research_query_weight")}
            </Grid>
          </Box>
          <FlexBetween gap={2.5} sx={{ mt: 1, justifyContent: "flex-end" }}>
            <Button
              type="button"
              className="btn-orange btn-dark"
              variant="contained"
              disabled={formik.isSubmitting}
              onClick={handleResetToDefaults}
              startIcon={<RestoreIcon />}
            >
              Reset to Defaults
            </Button>
            <ViewJsonSplitButton
              onViewJson={() => setIsPayloadDialogOpen(true)}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={formik.isSubmitting || (isEditMode && !formik.dirty)}
              startIcon={
                formik.isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
            >
              Save
            </Button>
          </FlexBetween>
        </form>
      </Box>
      <CommentBeforeUpdateDialog
        title="Confirm updating Pipeline Weight Config"
        dialogContent={
          <div>
            <p className="m-0 mb-2">Add a note before updating</p>
            <textarea
              value={comment}
              className="form-control-1 max-60"
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        }
        openConfirmation={openComment}
        handleSaveComment={handleSaveComment}
        handleCancel={() => {
          setOpenComment(false);
          setComment("");
          setPendingPayload(null);
        }}
      />
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Pipeline Weight Config Payload"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </>
  );
};

export default AddEditPipelineWeightConfig;
